import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-upload-link',
    templateUrl: './upload-link.component.html',
    styleUrls: ['./upload-link.component.scss'],
    standalone: false
})
export class UploadLinkComponent implements OnInit {
  @Input() isVisible: boolean = false;
  @Output() fileSelected = new EventEmitter<{
    file: File;
    base64: string;
    fileName: string;
  }>();
  @Output() modalClosed = new EventEmitter<void>();

  fileUrl: string = '';
  isProcessingLink: boolean = false;
  linkError: string = '';

  constructor(private messageService: MessageService) {}

  ngOnInit(): void {}

  closeLinkDialog() {
    this.isVisible = false;
    this.fileUrl = '';
    this.linkError = '';
    this.isProcessingLink = false;
    this.modalClosed.emit();
  }

  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  private getFileInfoFromUrl(url: string): { name: string; extension: string } {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'downloaded-file';
      const extension = filename.includes('.')
        ? filename.split('.').pop()?.toLowerCase() || ''
        : '';

      return {
        name: filename,
        extension: extension,
      };
    } catch {
      return {
        name: 'downloaded-file',
        extension: '',
      };
    }
  }

  private isSupportedFileType(extension: string): boolean {
    const supportedTypes = ['pdf'];
    return supportedTypes.includes(extension.toLowerCase());
  }

  private async urlToFile(url: string): Promise<File> {
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          Accept: '*/*',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const fileInfo = this.getFileInfoFromUrl(url);

      // Create File object from blob
      const file = new File([blob], fileInfo.name, {
        type: blob.type || this.getMimeType(fileInfo.extension),
      });

      return file;
    } catch (error) {
      throw new Error(
        `Failed to download file: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  private getMimeType(extension: string): string {
    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
    };
    return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
  }

  // Method to handle Google Drive links
  private convertGoogleDriveUrl(url: string): string {
    // Remove any trailing parameters that might interfere
    const cleanUrl =
      url.split('?')[0] + (url.includes('?') ? '?' + url.split('?')[1] : '');

    // Pattern 1: https://drive.google.com/file/d/FILE_ID/view
    const driveRegex =
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)/;
    const match = cleanUrl.match(driveRegex);

    if (match) {
      const fileId = match[1];
      // Use the more reliable uc endpoint
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    // Pattern 2: Any other Google Drive URL with id parameter
    const viewRegex = /[?&]id=([a-zA-Z0-9-_]+)/;
    const viewMatch = cleanUrl.match(viewRegex);

    if (viewMatch) {
      const fileId = viewMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    // If no match found, return original URL
    // console.warn('Could not parse Google Drive URL:', url);
    return url;
  }

  // Method to handle Dropbox links
  private convertDropboxUrl(url: string): string {
    // Convert Dropbox share URL to direct download URL
    if (url.includes('dropbox.com') && url.includes('?dl=0')) {
      return url.replace('?dl=0', '?dl=1');
    }
    return url;
  }

  // Method to handle OneDrive links
  private convertOneDriveUrl(url: string): string {
    // Convert OneDrive share URL to direct download URL
    if (url.includes('1drv.ms') || url.includes('onedrive.live.com')) {
      if (!url.includes('download=1')) {
        const separator = url.includes('?') ? '&' : '?';
        return url + separator + 'download=1';
      }
    }
    return url;
  }

  // Method to process the URL and convert to direct download link
  private processFileUrl(url: string): string {
    // Handle different cloud storage providers
    if (url.includes('drive.google.com')) {
      return this.convertGoogleDriveUrl(url);
    } else if (url.includes('dropbox.com')) {
      return this.convertDropboxUrl(url);
    } else if (url.includes('1drv.ms') || url.includes('onedrive.live.com')) {
      return this.convertOneDriveUrl(url);
    }

    return url;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsDataURL(file);
    });
  }

  // Main method to upload file from URL
// UploadLinkComponent - Updated uploadFromLink method
async uploadFromLink() {
  if (!this.fileUrl.trim()) {
    this.linkError = 'Please enter a valid URL';
    return;
  }

  if (!this.isValidUrl(this.fileUrl)) {
    this.linkError =
      'Please enter a valid URL (must start with http:// or https://)';
    return;
  }

  this.isProcessingLink = true;
  this.linkError = '';

  try {
    // Process the URL to handle cloud storage links
    const processedUrl = this.processFileUrl(this.fileUrl);

    // Get file info and validate type
    const fileInfo = this.getFileInfoFromUrl(processedUrl);

    if (!this.isSupportedFileType(fileInfo.extension)) {
      throw new Error(
        `Unsupported file type: ${fileInfo.extension}. Supported types: PDF`
      );
    }

    // Download the file and convert to File object
    const file = await this.urlToFile(processedUrl);

    // Convert File to base64
    const base64Data = await this.fileToBase64(file);

    // Emit the file data to parent component
    this.fileSelected.emit({
      file: file,
      base64: base64Data,
      fileName: file.name,
    });

    // Close the modal after successful upload
    this.closeLinkDialog();
    
  } catch (error) {
    this.linkError =
      error instanceof Error
        ? error.message
        : 'Failed to upload file from URL';
    this.messageService.add({
      severity: 'error',
      detail: this.linkError,
    });
  } finally {
    this.isProcessingLink = false;
  }
}


  onUrlInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.fileUrl = target.value;

    // Clear error when user starts typing
    if (this.linkError) {
      this.linkError = '';
    }
  }

  onUrlPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';

    if (this.isValidUrl(pastedText)) {
      this.fileUrl = pastedText;
      this.linkError = '';
    } else {
      this.linkError = 'Pasted text is not a valid URL';
    }
  }
}
